import React, { createContext, useContext, useEffect, useMemo } from 'react';
import {
	buildUnlayerClientContext,
	buildUnlayerDealContext,
	buildUnlayerUserContext,
	getClientIndustryId,
} from '@/components/Drawers/AddUnlayerEmailDrawer/helpers';
import { COMMERCIAL } from '@/components/forms/CreateNewBusinessForm/validationSchema';
import { useScopeOfAdviceInjectionOptions } from '@/components/Drawers/AddUnlayerEmailDrawer/hooks/useScopeOfAdviceInjectionOptions';
import { useGetClientPropertiesQuery } from '@/features/clients/clientsApi';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useGetDealPoliciesQuery, useGetDealRowQuery } from '@/features/deals/dealsApi';
import { useGetInsightClaimQuery } from '@/features/claims/ticketsApi';
import { useGetInsightObjectByIdQuery } from '@/features/properties/propertiesApi';
import { getUnlayerTags } from '@/utils/unlayer';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFormikContext } from 'formik';

const UnlayerMergeTagsContext = createContext(null);

/**
 * Builds editor merge tags and scope-of-advice injection options from the active
 * client/deal context while the user composes an email.
 */
export const UnlayerMergeTagsProvider = ({ children }) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const userBrokerGroupIds = userDetails?.brokerGroupArray ?? [];

	const { values } = useFormikContext();

	const selectedClientId = values.client?.clientId ?? null;
	const selectedDealId = values.deal?.dealId ?? null;

	const { data: dealRow } = useGetDealRowQuery(selectedDealId, {
		skip: !selectedDealId,
	});

	const isClaimContext = Boolean(
		dealRow?.isClaim ||
		values.deal?.isClaim ||
		values.deal?.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS,
	);

	const { data: insightClaim } = useGetInsightClaimQuery(selectedDealId, {
		skip: !selectedDealId || !isClaimContext,
	});

	const { data: clientPropertiesResponse } = useGetClientPropertiesQuery(selectedClientId, {
		skip: !selectedClientId,
	});

	const { data: classesOfRisk = [] } = useGetClassesOfRiskQuery(
		{
			countryCode: userDetails?.broker?.country ?? 'NZ',
			clientType: dealRow?.clientType ?? values.deal?.clientType ?? COMMERCIAL,
			brokerGroupIds: userBrokerGroupIds,
		},
		{
			skip: !userDetails,
		},
	);

	const { data: policyData } = useGetDealPoliciesQuery(
		{ dealId: selectedDealId, showLatest: true },
		{ skip: !selectedDealId },
	);

	const clientDetails = useMemo(
		() => ({
			clientRow: {
				...(values.client ?? {}),
				...(clientPropertiesResponse?.clientRow ?? {}),
			},
			insight: clientPropertiesResponse?.insight,
		}),
		[clientPropertiesResponse, values.client],
	);

	const industryId = getClientIndustryId(clientDetails);
	const { currentData: industry } = useGetInsightObjectByIdQuery(
		{ objectType: 'IndustryType', id: industryId },
		{ skip: !industryId },
	);

	const { getFeatureFlags } = useFeatureFlagContext();

	const isMarble =
		userDetails?.broker?.ledger === 'marble' || Boolean(getFeatureFlags('marbleSOA')?.active);

	const unlayerUserContext = useMemo(
		() =>
			buildUnlayerUserContext({
				selectedFrom: values.from,
				userDetails,
				fallbackEmail: email,
			}),
		[email, userDetails, values.from],
	);

	const unlayerClientContext = useMemo(
		() =>
			buildUnlayerClientContext({
				clientDetails,
				industryName: industry?.Name,
			}),
		[clientDetails, industry?.Name],
	);

	const unlayerDealContext = useMemo(
		() =>
			buildUnlayerDealContext({
				dealDetails: {
					...(dealRow ?? {}),
					...(values.deal ?? {}),
					...(insightClaim ?? {}),
				},
				classesOfRisk,
				policies: policyData ?? [],
			}),
		[classesOfRisk, dealRow, insightClaim, values.deal, policyData],
	);

	const mergeTags = useMemo(
		() => getUnlayerTags(unlayerUserContext, unlayerClientContext, unlayerDealContext),
		[unlayerClientContext, unlayerDealContext, unlayerUserContext],
	);

	const scopeOfAdviceClientEnvelope = useMemo(
		() => ({
			...unlayerClientContext,
		}),
		[unlayerClientContext],
	);

	const scopeOfAdviceDealContext = useMemo(
		() => ({
			...(dealRow ?? {}),
			...(values.deal ?? {}),
			...(insightClaim ?? {}),
		}),
		[dealRow, insightClaim, values.deal],
	);

	const buildScopeOfAdviceInjectionOptions = useScopeOfAdviceInjectionOptions({
		classesOfRisk,
		isMarble,
		scopeOfAdviceClientEnvelope,
		scopeOfAdviceDealContext,
		userDetails,
	});

	const value = useMemo(
		() => ({
			buildScopeOfAdviceInjectionOptions,
			mergeTags,
		}),
		[buildScopeOfAdviceInjectionOptions, mergeTags],
	);

	return (
		<UnlayerMergeTagsContext.Provider value={value}>
			{children}
		</UnlayerMergeTagsContext.Provider>
	);
};

export const useUnlayerMergeTagsContext = () => {
	const context = useContext(UnlayerMergeTagsContext);
	if (!context) {
		throw new Error(
			'useUnlayerMergeTagsContext must be used within an UnlayerMergeTagsProvider',
		);
	}
	return context;
};
